import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalBody,
} from 'reactstrap';
import PageBreadcrumb from '../../Components/Common/PageBreadcrumb';
import DataTable from 'react-data-table-component';
import ActionButton from '../../Components/Common/ActionButton';
import DeleteModal from '../../Components/Common/DeleteModal';
import ListPager from '../../Components/Common/ListPager';
import {
  ActiveFilterChips,
  ClearFiltersButton,
  ListFilterSelect,
  ListSearch,
} from '../../Components/Common/ListFilters';
import { EmptyState, SkeletonRows } from '../../Components/Common/ListStates';
import notify, { MESSAGES } from '../../utils/notify';
import '../TenderOpening/components/TenderOpening.css';
import axios from 'axios';
import { api } from '../../config';
import { getAuthToken } from '../../auth/tokenvalidation';
import {
  FormGrid,
  SelectField,
  TextAreaField,
  TextField,
} from '../../Components/Form';
import StatusBadge from '../../Components/Common/StatusBadge';
import {
  PUBLISH_PERIOD_MONTHLY,
  PublishPeriodTypeOptions,
} from '../../constants/masterData';

const publishPeriodLabel = value =>
  PublishPeriodTypeOptions.find(option => option.value === value)?.label ?? '-';

const LPG_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

const IndexPriceList = () => {
  const token = getAuthToken();

  const [data, setData] = useState({ items: [] });
  const [deleteModal, setDeleteModal] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [modal, setModal] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [modalIndexPrice, setModalIndexPrice] = useState(false);
  const [indexPriceFormData, setIndexPriceFormData] = useState({
    name: null,
    description: null,
    isLpgCase: null,
    publishPeriodType: PUBLISH_PERIOD_MONTHLY,
    id: null,
  });

  const [filterText, setFilterText] = useState('');
  const [search, setSearch] = useState('');
  const requestRef = useRef(0);
  const [lpgFilter, setLpgFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const startEntry = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endEntry = Math.min(currentPage * rowsPerPage, totalRows);

  const handleDeleteModal = id => {
    const selectedRow = data.items.find(item => item.id === id);
    setInvoice(selectedRow);
    setDeleteModal(true);
  };

  const handleDeleteInvoice = async () => {
    if (invoice) {
      try {
        const response = await fetch(
          `${api.API_URL}/indexprice/${invoice.id}`,
          {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.message || `HTTP error! status: ${response.status}`
          );
        }

        notify.success('Index Price deleted successfully.');
        setDeleteModal(false);
        setInvoice(null);
        fetchData(currentPage, rowsPerPage, token, search);
      } catch (err) {
        notify.apiError(err, 'delete index price');
      }
    }
  };

  const toggleIndexPriceModal = () => {
    setModal(!modal);
  };

  const columns = [
    {
      id: 'name',
      name: 'Index Price Name',
      selector: row => row.name,
      sortable: true,
      width: '25%',
      cell: row => (
        <span className='to-title' title={String(row.name ?? '')}>
          {row.name ?? '-'}
        </span>
      ),
    },

    {
      id: 'description',
      name: 'Description',
      selector: row => row.description,
      sortable: true,
      width: '35%',
      cell: row => (
        <span className='pv-desc' title={String(row.description ?? '')}>
          {row.description ?? '-'}
        </span>
      ),
    },

    {
      id: 'isLpgCase',
      name: 'LPG Case',
      selector: row => row.isLpgCase,
      sortable: true,
      width: '10%',
      cell: row =>
        row.isLpgCase === true || row.isLpgCase === false ? (
          <StatusBadge
            tone={row.isLpgCase ? 'success' : 'muted'}
            label={row.isLpgCase ? 'Yes' : 'No'}
          />
        ) : (
          '-'
        ),
    },

    {
      id: 'publishPeriodType',
      name: 'Publish Period',
      selector: row => row.publishPeriodType,
      sortable: true,
      width: '15%',
      cell: row => publishPeriodLabel(row.publishPeriodType),
    },

    {
      name: <span className='text-center w-100'>Action</span>,
      width: '15%',
      center: true,
      cell: row => (
        <div className='d-flex gap-1 justify-content-center w-100'>
          <ActionButton
            variant='icon'
            size='sm'
            icon='ri-edit-line'
            label='Edit'
            onClick={() => handleUpdateModal(row.id)}
          />
          <ActionButton
            variant='icon'
            size='sm'
            danger
            icon='ri-delete-bin-line'
            label='Delete'
            confirm={false}
            onClick={() => handleDeleteModal(row.id)}
          />
        </div>
      ),
    },
  ];

  document.title = 'Data Index Price';

  const handleChange = e => {
    const { name, value } = e.target;
    setIndexPriceFormData({ ...indexPriceFormData, [name]: value });
  };

  // Debounce typing so each keystroke is not a request.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(filterText.trim());
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [filterText]);

  useEffect(() => {
    fetchData(currentPage, rowsPerPage, token, search);
    // fetchData reads lpgFilter from the render it was created in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, rowsPerPage, token, search, lpgFilter]);

  const handleLpgFilterChange = value => {
    setLpgFilter(value);
    setCurrentPage(1);
  };

  const handleFilterChange = e => {
    setFilterText(e.target.value);
    // Clearing applies at once; typing waits for the debounce.
    if (!e.target.value) {
      setSearch('');
      setCurrentPage(1);
    }
  };

  const clearFilter = () => {
    setFilterText('');
    setSearch('');
    setCurrentPage(1);
    setLpgFilter('');
  };

  const filterChips = [
    filterText && {
      key: 'search',
      label: 'Search',
      value: filterText,
      onRemove: () => handleFilterChange({ target: { value: '' } }),
    },
    lpgFilter && {
      key: 'lpg',
      label: 'LPG Case',
      value: LPG_OPTIONS.find(o => o.value === lpgFilter)?.label ?? lpgFilter,
      onRemove: () => handleLpgFilterChange(''),
    },
  ].filter(Boolean);
  const hasFilters = filterChips.length > 0;

  const fetchData = async (currentPage, rowsPerPage, token, search) => {
    const reqId = ++requestRef.current;
    setLoading(true);
    fetch(
      `${api.API_URL}/indexprice?PageNumber=${currentPage}&PageSize=${rowsPerPage}${search ? `&Search=${encodeURIComponent(search)}` : ''}${lpgFilter ? `&IsLpgCase=${lpgFilter === 'yes'}` : ''}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'same-origin',
      }
    )
      .then(data => data.json())
      .then(
        result => {
          if (reqId !== requestRef.current) return; // stale response
          setData({
            items: result.data.items || [],
          });
          setTotalRows(result.data.totalItems || 0);
        },
        _error => {
          // Handle error appropriately
        }
      )
      .catch(_error => {
        // Handle error silently or show appropriate message
      })
      .finally(() => {
        if (reqId === requestRef.current) setLoading(false);
      });
  };

  const handlePageChange = useCallback(page => {
    setCurrentPage(page);
  }, []);

  const handlePerRowsChange = useCallback(newPerPage => {
    setRowsPerPage(newPerPage);
    setCurrentPage(1);
  }, []);

  const handleNewModal = () => {
    setIndexPriceFormData({
      name: null,
      description: null,
      isLpgCase: null,
      publishPeriodType: PUBLISH_PERIOD_MONTHLY,
      id: null,
    });
    indexPriceModal();
  };

  const handleUpdateModal = async (mid = null) => {
    await fetchIndexPriceDetail(mid);
    indexPriceModal();
  };

  const indexPriceModal = () => {
    setModalIndexPrice(!modalIndexPrice);
  };

  const selectedFieldsIndexPrice = {
    name: indexPriceFormData.name,
    description: indexPriceFormData.description,
    isLpgCase: indexPriceFormData.isLpgCase,
    publishPeriodType: indexPriceFormData.publishPeriodType,
  };

  const handleAddDataIndexPrice = async () => {
    try {
      await axios.post(`${api.API_URL}/indexprice`, selectedFieldsIndexPrice, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      notify.success('Index Price added successfully.');
    } catch (error) {
      notify.apiError(error, 'save data');
    } finally {
      fetchData(currentPage, rowsPerPage, token, search);
    }
  };

  const fetchIndexPriceDetail = async mid => {
    try {
      const response = await axios.get(`${api.API_URL}/indexprice/${mid}`, {
        withXSRFToken: true,
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: `application/json`,
        },
      });
      const data = response?.data?.data ?? response?.data ?? response;
      setIndexPriceFormData({
        name: data.name || null,
        description: data.description || null,
        isLpgCase: data.isLpgCase ?? null,
        publishPeriodType: data.publishPeriodType || PUBLISH_PERIOD_MONTHLY,
        id: data.id || null,
      });
    } catch (error) {
      // Handle error silently or show appropriate message
    }
  };

  const handleUpdDataIndexPrice = async mid => {
    try {
      await axios.put(
        `${api.API_URL}/indexprice/${mid}`,
        selectedFieldsIndexPrice,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      notify.success(MESSAGES.updated);
    } catch (error) {
      notify.apiError(error, 'save data');
    } finally {
      fetchData(currentPage, rowsPerPage, token, search);
    }
  };

  return (
    <React.Fragment>
      <div className='page-content'>
        <DeleteModal
          show={deleteModal}
          onDeleteClick={() => handleDeleteInvoice()}
          onCloseClick={() => setDeleteModal(false)}
          title='Delete this index price?'
          message='It will be permanently deleted. This cannot be undone.'
          subject={invoice?.name}
          confirmLabel='Delete index price'
        />
        <Container fluid>
          <div className='to-page email-wrapper d-lg-flex gap-1 mx-n4 mt-n4 p-1'>
            <div className='email-content minimal-border'>
              <div className='p-4'>
                <div className='to-hero mb-4'>
                  <PageBreadcrumb
                    fallback={{
                      label: 'Index Price',
                      to: '/apps-indexprice/view',
                    }}
                  />

                  <div className='d-flex align-items-center gap-3 flex-wrap'>
                    <span className='to-hero-icon'>
                      <i className='ri-line-chart-line' aria-hidden='true' />
                    </span>
                    <div className='flex-grow-1'>
                      <h4 className='to-hero-title'>Index Price</h4>
                      <p className='to-hero-subtitle'>
                        Maintain the index prices used as price references.
                      </p>
                    </div>
                    <div className='d-flex gap-2 flex-wrap'>
                      <button
                        type='button'
                        className='to-hero-btn'
                        onClick={() =>
                          fetchData(currentPage, rowsPerPage, token, search)
                        }
                      >
                        <i className='ri-refresh-line' aria-hidden='true' />
                        Refresh
                      </button>
                      <button
                        type='button'
                        className='to-hero-btn to-hero-btn--solid'
                        onClick={() => handleNewModal()}
                      >
                        <i className='ri-add-line' aria-hidden='true' />
                        Create New Index Price
                      </button>
                    </div>
                  </div>
                </div>

                <div className='to-panel'>
                  <div className='to-toolbar'>
                    <ListSearch
                      value={filterText}
                      onChange={handleFilterChange}
                      placeholder='Search index prices...'
                      ariaLabel='Search index prices'
                    />
                    <ListFilterSelect
                      label='LPG Case'
                      value={lpgFilter}
                      onChange={handleLpgFilterChange}
                      allLabel='All'
                      options={LPG_OPTIONS}
                      ariaLabel='Filter by LPG case'
                    />
                    <ClearFiltersButton
                      count={filterChips.length}
                      onClick={clearFilter}
                    />
                    <span className='to-toolbar-meta'>
                      {loading
                        ? 'Loading...'
                        : `Showing ${startEntry}–${endEntry} of ${totalRows} index prices`}
                    </span>
                  </div>

                  <ActiveFilterChips chips={filterChips} />

                  <div className='to-table-wrap'>
                    <DataTable
                      columns={columns}
                      data={data.items}
                      highlightOnHover
                      persistTableHead
                      defaultSortFieldId='name'
                      defaultSortAsc
                      progressPending={loading}
                      progressComponent={<SkeletonRows />}
                      noDataComponent={
                        <EmptyState
                          title='No index price found'
                          emptyHint='No index prices have been registered yet. Index prices added here become available as price references.'
                          filteredHint='No index price matches the current filters.'
                          hasFilters={hasFilters}
                          onClear={clearFilter}
                          action={{
                            label: 'Create New Index Price',
                            onClick: handleNewModal,
                          }}
                        />
                      }
                      customStyles={{
                        headCells: {
                          style: { paddingLeft: '12px', paddingRight: '12px' },
                        },
                        cells: {
                          style: {
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            wordBreak: 'break-word',
                          },
                        },
                        table: { style: { width: '100%' } },
                      }}
                    />
                  </div>

                  <ListPager
                    currentPage={currentPage}
                    rowsPerPage={rowsPerPage}
                    totalRows={totalRows}
                    isLoading={loading}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handlePerRowsChange}
                  />
                </div>
              </div>
            </div>
          </div>

          <Modal
            isOpen={modalIndexPrice}
            role='dialog'
            autoFocus={true}
            centered
            id='addIndexPriceModal'
            toggle={toggleIndexPriceModal}
            data-bs-backdrop='static'
            size='sm'
          >
            <ModalHeader
              className='bg-soft-info'
              toggle={() => {
                setModalIndexPrice(!modalIndexPrice);
              }}
            >
              <span className='modal-title text-info fw-bold'>
                {indexPriceFormData.id !== null
                  ? 'Edit Index Price'
                  : 'Add Index Price'}
              </span>
            </ModalHeader>

            <ModalBody>
              <FormGrid columns={2}>
                <TextField
                  label='Index price name'
                  name='name'
                  placeholder='e.g. MOPS 92'
                  value={indexPriceFormData.name}
                  onChange={handleChange}
                />
                <SelectField
                  label='LPG case'
                  name='isLpgCase'
                  options={[
                    { value: 'true', label: 'Yes' },
                    { value: 'false', label: 'No' },
                  ]}
                  value={
                    indexPriceFormData.isLpgCase === null ||
                    indexPriceFormData.isLpgCase === undefined
                      ? ''
                      : indexPriceFormData.isLpgCase.toString()
                  }
                  onChange={value =>
                    setIndexPriceFormData(prev => ({
                      ...prev,
                      isLpgCase: value === null ? null : value === 'true',
                    }))
                  }
                />
                <SelectField
                  span='full'
                  label='Publish period'
                  name='publishPeriodType'
                  hint='How a procurement sets this index: a start and end date, or one publish month.'
                  required
                  clearable={false}
                  searchable={false}
                  options={PublishPeriodTypeOptions}
                  value={indexPriceFormData.publishPeriodType}
                  onChange={value =>
                    setIndexPriceFormData(prev => ({
                      ...prev,
                      publishPeriodType: value ?? PUBLISH_PERIOD_MONTHLY,
                    }))
                  }
                />
                <TextAreaField
                  span='full'
                  label='Description'
                  name='description'
                  rows={5}
                  value={indexPriceFormData.description}
                  onChange={handleChange}
                />
              </FormGrid>
            </ModalBody>

            <ModalFooter>
              <ActionButton
                variant='secondary'
                size='sm'
                icon='ri-close-line'
                onClick={() => {
                  setModalIndexPrice(!modalIndexPrice);
                }}
              >
                Close
              </ActionButton>
              <ActionButton
                variant='primary'
                size='sm'
                icon='ri-save-line'
                onClick={async () => {
                  await (indexPriceFormData.id !== null
                    ? handleUpdDataIndexPrice(indexPriceFormData.id)
                    : handleAddDataIndexPrice());
                  setModalIndexPrice(false);
                }}
              >
                {indexPriceFormData.id !== null ? 'Update' : 'Save'}
              </ActionButton>
            </ModalFooter>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default IndexPriceList;
